import clsx from 'clsx';
import type { FunctionComponent } from 'react';
import { useCallback, useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { Button } from '~/ui/core/button/Button';
import { IconCrossSmall } from '~/ui/core/icons';

import type { AnnouncementContentProps } from '../types';
import backgroundImage from './background-2.webp';
import commentRegenerationImage from './comment-regeneration.webp';
import commentSelectionImage from './comment-selection.webp';
import versionNoteInputImage from './version-note-input.webp';

type Step = {
    readonly descriptionKey: TranslationKey;
    readonly headlineKey: TranslationKey;
    readonly image: string;
    readonly titleKey: TranslationKey;
};

const STEPS: readonly Step[] = [
    {
        descriptionKey: 'announcements.horizon_1_3_comments_job_architect.version_comments.description',
        headlineKey: 'announcements.horizon_1_3_comments_job_architect.version_comments.headline',
        image: versionNoteInputImage,
        titleKey: 'announcements.horizon_1_3_comments_job_architect.version_comments.title',
    },
    {
        descriptionKey: 'announcements.horizon_1_3_comments_job_architect.collaborative_comments.description',
        headlineKey: 'announcements.horizon_1_3_comments_job_architect.collaborative_comments.headline',
        image: commentSelectionImage,
        titleKey: 'announcements.horizon_1_3_comments_job_architect.collaborative_comments.title',
    },
    {
        descriptionKey: 'announcements.horizon_1_3_comments_job_architect.comment_regeneration.description',
        headlineKey: 'announcements.horizon_1_3_comments_job_architect.comment_regeneration.headline',
        image: commentRegenerationImage,
        titleKey: 'announcements.horizon_1_3_comments_job_architect.comment_regeneration.title',
    },
];

type StepDotProps = {
    readonly index: number;
    readonly isActive: boolean;
    readonly onClick: () => void;
};

const StepDot: FunctionComponent<StepDotProps> = ({ index, isActive, onClick }) => (
    <button
        className="group relative flex h-8 w-4 items-center justify-center border-none bg-transparent p-0 cursor-pointer"
        aria-label={`Go to slide ${index + 1}`}
        type="button"
        onClick={onClick}
    >
        <span
            className={clsx(
                'block h-2 w-2 rounded-full transition-[background-color,scale] duration-200 group-hover:bg-gray-500',
                isActive ? 'scale-100 bg-black-800' : 'scale-90 bg-gray-300',
            )}
        />
    </button>
);

export const Horizon13Content: FunctionComponent<AnnouncementContentProps> = ({ onDismiss }) => {
    const translate = useTranslate();
    const [step, setStep] = useState(0);

    const handleBack = useCallback(() => setStep((s) => s - 1), []);
    const handleNext = useCallback(() => setStep((s) => s + 1), []);
    const activeStep = STEPS[step];

    if (activeStep === undefined) {
        return null;
    }

    return (
        <div className="flex h-[610px] flex-col bg-white-200 p-xxl">
            <div className="relative flex h-[380px] shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-100 px-4xl py-l">
                <img
                    alt=""
                    aria-hidden={true}
                    className="absolute inset-0 h-full w-full object-cover object-[center_65%]"
                    src={backgroundImage}
                />
                <div className="absolute right-l top-l z-20">
                    <Button
                        aria-label={translate('common.close')}
                        className="!text-white-200 hover:!text-gray-800 [&>svg]:!text-white-200 hover:[&>svg]:!text-gray-800"
                        iconOnly={true}
                        size="sm"
                        variant="tertiary"
                        onClick={onDismiss}
                    >
                        <IconCrossSmall />
                    </Button>
                </div>
                <div className="relative z-10 mx-auto flex h-full w-full max-w-[390px] items-center justify-center">
                    <img
                        alt={translate(activeStep.titleKey)}
                        className="block max-h-[300px] w-full object-contain"
                        src={activeStep.image}
                    />
                </div>
            </div>

            <div className="flex flex-1 flex-col pt-xxl">
                <div className="flex h-[118px] flex-col items-start justify-start">
                    <h2 className="m-0 max-w-full text-balance text-xl font-normal tracking-[-0.05rem] text-black-800">
                        {translate(activeStep.headlineKey)}
                    </h2>
                    <p className="m-0 mt-l max-w-full text-pretty text-sm font-normal leading-relaxed text-gray-800">
                        {translate(activeStep.descriptionKey)}
                    </p>
                </div>

                <div className="mt-auto flex items-center justify-between gap-l">
                    <nav className="flex items-center" aria-label="Announcement slides">
                        {STEPS.map((s, i) => (
                            <StepDot key={s.titleKey} index={i} isActive={step === i} onClick={() => setStep(i)} />
                        ))}
                    </nav>

                    <div className="flex items-center justify-end gap-xs">
                        {step > 0 && (
                            <Button variant="tertiary" onClick={handleBack}>
                                {translate('announcements.back')}
                            </Button>
                        )}
                        {step < STEPS.length - 1 ? (
                            <Button onClick={handleNext}>{translate('announcements.continue')}</Button>
                        ) : (
                            <Button type="button" onClick={onDismiss}>
                                {translate('announcements.get_started')}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const ANNOUNCEMENT_CONTENT: Record<string, FunctionComponent<AnnouncementContentProps>> = {
    horizon_1_3_comments_job_architect: Horizon13Content,
};
